import React, { useState } from 'react';

const LandingPage = ({ transactions = [], transactionTypes = [], onSelectTransaction, onCreateNew }) => {
  const [viewMode, setViewMode] = useState('card');
  const [filters, setFilters] = useState({
    searchTerm: '',
    txnType: 'All',
    startDate: '',
    endDate: ''
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const resetFilters = () => {
    setFilters({ searchTerm: '', txnType: 'All', startDate: '', endDate: '' });
  };

  const filteredTransactions = (transactions || [])
    .filter(t => filters.txnType === 'All' || t.txnTypeName === filters.txnType)
    .filter(t => {
      if (!filters.startDate || !filters.endDate) return true;
      const docDate = new Date(t.docDate);
      return docDate >= new Date(filters.startDate) && docDate <= new Date(filters.endDate);
    })
    .filter(t =>
      t.docNumber.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
      t.partyName.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
      t.mode.toLowerCase().includes(filters.searchTerm.toLowerCase())
    );

  const recordCount = filteredTransactions.length;
  const totalValue = filteredTransactions.reduce((sum, txn) => sum + txn.netTotal, 0);

  const StatusBadge = ({ status, mode }) => {
    let text = mode;
    let color = 'bg-yellow-100 text-yellow-800';
    if (mode === 'Finished') {
      if (status.verified) { text = 'Verified'; color = 'bg-green-100 text-green-800'; }
      else if (status.checked) { text = 'Checked'; color = 'bg-blue-100 text-blue-800'; }
    }
    return <span className={`px-2 py-1 text-xs font-medium rounded-full ${color}`}>{text}</span>;
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex flex-col justify-between gap-4 rounded-3xl border border-emerald-100 bg-white/90 p-5 shadow-xl shadow-slate-900/5 backdrop-blur md:flex-row md:items-center">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wide text-emerald-600">Workspace</p>
          <h1 className="mt-1 text-3xl font-black tracking-tight text-slate-900">Transactions</h1>
        </div>
        <button onClick={onCreateNew} className="w-full rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-3 font-semibold text-white shadow-lg shadow-emerald-700/20 transition hover:from-emerald-700 hover:to-teal-700 md:w-auto">
          Create New Transaction
        </button>
      </div>

      <div className="space-y-4 rounded-2xl border border-slate-100 bg-white p-4 shadow-xl shadow-slate-900/5">
        <div className="flex items-center justify-between">
          <button onClick={() => setIsFilterOpen(!isFilterOpen)} className="flex items-center gap-2 font-semibold text-emerald-700">
            <span>Show Filters</span>
            <svg className={`w-5 h-5 transform transition-transform ${isFilterOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
            </svg>
          </button>
          <div className="flex items-center gap-2 rounded-xl bg-slate-100 p-1">
            <button onClick={() => setViewMode('card')} className={`rounded-lg px-3 py-2 text-sm transition-colors ${viewMode === 'card' ? 'bg-white text-emerald-700 shadow' : 'bg-transparent text-slate-600'}`}>
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor"><path d="M5 3a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2V5a2 2 0 00-2-2H5zm0 2h10v10H5V5z" /></svg>
            </button>
            <button onClick={() => setViewMode('table')} className={`rounded-lg px-3 py-2 text-sm transition-colors ${viewMode === 'table' ? 'bg-white text-emerald-700 shadow' : 'bg-transparent text-slate-600'}`}>
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" /></svg>
            </button>
          </div>
        </div>

        {isFilterOpen && (
          <div className="space-y-4 border-t border-slate-100 pt-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
              <input type="text" name="searchTerm" placeholder="Search..." value={filters.searchTerm} onChange={handleFilterChange} className="w-full rounded-xl border border-slate-200 p-3 text-slate-800 outline-none" />
              <select name="txnType" value={filters.txnType} onChange={handleFilterChange} className="w-full rounded-xl border border-slate-200 p-3 text-slate-800 outline-none">
                <option value="All">All Types</option>
                {(transactionTypes || []).map(type => <option key={type.id} value={type.name}>{type.name}</option>)}
              </select>
              <input type="date" name="startDate" value={filters.startDate} onChange={handleFilterChange} className="w-full rounded-xl border border-slate-200 p-3 text-slate-800 outline-none" />
              <input type="date" name="endDate" value={filters.endDate} onChange={handleFilterChange} className="w-full rounded-xl border border-slate-200 p-3 text-slate-800 outline-none" />
            </div>
            <div className="flex justify-end">
              <button onClick={resetFilters} className="rounded-xl bg-slate-100 px-4 py-2 font-semibold text-slate-700 transition hover:bg-slate-200">Reset</button>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-5 text-center">
          <p className="text-sm font-semibold text-emerald-700">Record Count</p>
          <p className="text-3xl font-black text-emerald-950">{recordCount}</p>
        </div>
        <div className="rounded-2xl border border-sky-100 bg-sky-50 p-5 text-center">
          <p className="text-sm font-semibold text-sky-700">Total Value</p>
          <p className="text-3xl font-black text-sky-950">₹{totalValue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
        </div>
      </div>

      {viewMode === 'card' ? (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {(filteredTransactions || []).map(txn => (
            <div key={txn.id} onClick={() => onSelectTransaction(txn.id)} className="cursor-pointer space-y-3 rounded-2xl border border-slate-100 bg-white p-5 shadow-xl shadow-slate-900/5 transition hover:-translate-y-0.5 hover:border-emerald-200 hover:shadow-2xl hover:shadow-emerald-950/10">
              <div className="flex items-start justify-between">
                <h2 className="text-lg font-bold text-slate-900">{txn.docNumber}</h2>
                <StatusBadge status={txn.status} mode={txn.mode} />
              </div>
              <p className="text-slate-600">{txn.partyName}</p>
              <p className="text-sm text-slate-500">{txn.docDate}</p>
              <p className="text-right text-xl font-black text-emerald-700">₹{txn.netTotal.toFixed(2)}</p>
            </div>
          ))}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-slate-100 bg-white shadow-xl shadow-slate-900/5">
          <table className="min-w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left font-bold text-slate-600">Doc No</th>
                <th className="px-4 py-3 text-left font-bold text-slate-600">Date</th>
                <th className="px-4 py-3 text-left font-bold text-slate-600">Party</th>
                <th className="px-4 py-3 text-right font-bold text-slate-600">Net Total</th>
                <th className="px-4 py-3 text-center font-bold text-slate-600">Status</th>
              </tr>
            </thead>
            <tbody>
              {(filteredTransactions || []).map(txn => (
                <tr key={txn.id} onClick={() => onSelectTransaction(txn.id)} className="cursor-pointer border-t border-slate-100 hover:bg-emerald-50/60">
                  <td className="px-4 py-3 font-semibold text-slate-900">{txn.docNumber}</td>
                  <td className="px-4 py-3 text-slate-600">{txn.docDate}</td>
                  <td className="px-4 py-3 text-slate-600">{txn.partyName}</td>
                  <td className="px-4 py-3 text-right font-bold text-slate-900">₹{txn.netTotal.toFixed(2)}</td>
                  <td className="px-4 py-3 text-center"><StatusBadge status={txn.status} mode={txn.mode} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
